import React, { useState, useEffect } from "react";
import { Building, ExternalLink } from "lucide-react";

const SubsidiariesSection = () => {
  const subsidiaries = [
    {
      id: "malikia",
      name: "Malikia Crest Consulting Engineers",
      logo: "/malikiaLogo.jpg",
      tagline: "Engineering Excellence Across Africa",
      description:
        "Leading consulting engineering firm specializing in infrastructure development, civil engineering, and project management across Africa with a focus on sustainable and innovative solutions.",
      services: [
        "Infrastructure Design",
        "Project Management",
        "Civil Engineering",
        "Environmental Assessment",
        "Structural Analysis",
        "Urban Planning",
      ],
      established: "2020",
      status: "active",
    },
  ];

  return (
    <section
      id="subsidiaries"
      className="py-24 bg-gradient-to-br from-emerald-50/50 via-blue-50/30 to-slate-50 relative overflow-hidden"
    >
      {/* Background elements */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-emerald-400/10 to-blue-400/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-48 h-48 bg-gradient-to-br from-yellow-400/10 to-emerald-400/10 rounded-full blur-2xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="inline-block p-3 bg-gradient-to-r from-emerald-500/10 to-blue-500/10 rounded-full mb-6 border border-emerald-200">
            <Building className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-5xl md:text-7xl font-black text-slate-900 mb-8">
            Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-blue-600 to-yellow-500">
              Subsidiaries
            </span>
          </h2>
          <div className="w-32 h-1.5 bg-gradient-to-r from-emerald-500 via-blue-500 to-yellow-500 mx-auto mb-10 rounded-full"></div>
          <p className="text-2xl text-slate-600 max-w-4xl mx-auto font-light">
            Each subsidiary represents our commitment to excellence in their
            respective industries, united under our shared vision for African
            prosperity.
          </p>
        </div>

        <div className="grid lg:grid-cols-1 gap-12 mb-16">
          {subsidiaries.map((subsidiary, index) => (
            <div key={subsidiary.id} className="group">
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 border border-white/50 overflow-hidden">
                <div className="p-12">
                  <div className="flex flex-col lg:flex-row items-start space-y-8 lg:space-y-0 lg:space-x-12">
                    {/* Logo Section - Enhanced Design */}
                    <div className="flex-shrink-0">
                      <div className="relative">
                        {/* Logo container with gradient background */}
                        <div className="w-48 h-48 lg:w-56 lg:h-56 bg-gradient-to-br from-emerald-50 via-blue-50 to-yellow-50 rounded-2xl p-6 border-2 border-gradient-to-br from-emerald-200 to-blue-200 shadow-lg group-hover:shadow-xl transition-all duration-300">
                          <div className="w-full h-full bg-white rounded-xl shadow-inner flex items-center justify-center overflow-hidden">
                            <img
                              src={subsidiary.logo}
                              alt={`${subsidiary.name} logo`}
                              className="max-w-[90%] max-h-[90%] object-contain transform group-hover:scale-110 transition-transform duration-500"
                              onError={(e) => {
                                // Fallback if image doesn't load
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                            {/* Fallback display */}
                            <div className="hidden w-full h-full bg-gradient-to-br from-emerald-100 to-blue-100 rounded-lg items-center justify-center">
                              <Building className="w-16 h-16 text-emerald-600" />
                            </div>
                          </div>
                        </div>

                        {/* Decorative elements around logo */}
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-yellow-400 to-emerald-400 rounded-full animate-pulse"></div>
                        <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-gradient-to-br from-blue-400 to-emerald-400 rounded-full animate-bounce delay-700"></div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="flex-1 space-y-8">
                      {/* Company Header */}
                      <div>
                        <h3 className="text-3xl lg:text-4xl font-black text-slate-900 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-600 group-hover:via-blue-600 group-hover:to-yellow-500 transition-all duration-300 mb-3">
                          {subsidiary.name}
                        </h3>
                        <p className="text-xl text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-blue-600 font-bold mb-4">
                          {subsidiary.tagline}
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="bg-gradient-to-r from-emerald-500 to-blue-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                            Established {subsidiary.established}
                          </span>
                          <span className="bg-gradient-to-r from-yellow-400 to-emerald-400 text-slate-900 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                            Active
                          </span>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-slate-600 leading-relaxed text-lg">
                        {subsidiary.description}
                      </p>

                      {/* Services Grid */}
                      <div>
                        <h4 className="font-black text-slate-900 mb-4 text-xl">
                          Core Services:
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {subsidiary.services.map((service, serviceIndex) => (
                            <div
                              key={serviceIndex}
                              className="bg-gradient-to-r from-emerald-50 to-blue-50 text-emerald-800 px-4 py-3 rounded-xl text-sm font-bold border border-emerald-200/50 text-center hover:shadow-lg hover:scale-105 transition-all duration-300"
                            >
                              {service}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Action Section */}
                      <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 pt-6 border-t border-slate-200">
                        <button className="group bg-gradient-to-r from-slate-900 via-blue-900 to-emerald-900 hover:from-emerald-600 hover:via-blue-600 hover:to-yellow-500 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center space-x-3 shadow-xl hover:shadow-2xl transform hover:scale-105">
                          <span>Explore Details</span>
                          <ExternalLink className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
                        </button>

                        <div className="text-slate-400 flex items-center space-x-2">
                          <span className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 via-emerald-500 to-blue-500">
                            01
                          </span>
                          <span className="text-lg font-medium">
                            of many to come
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Coming Soon Card */}
        <div className="bg-gradient-to-br from-yellow-50/50 via-emerald-50/30 to-blue-50/50 rounded-3xl border-2 border-dashed border-yellow-400/50 p-12 text-center shadow-xl backdrop-blur-sm">
          <div className="w-32 h-32 bg-gradient-to-br from-yellow-200/50 to-emerald-200/50 rounded-full flex items-center justify-center mx-auto mb-8 border-2 border-dashed border-emerald-300">
            <Building className="w-16 h-16 text-emerald-700" />
          </div>
          <h3 className="text-4xl font-black text-slate-900 mb-6">
            More Subsidiaries Coming Soon
          </h3>
          <p className="text-slate-600 mb-8 text-xl max-w-2xl mx-auto leading-relaxed">
            We're constantly expanding our portfolio with innovative companies
            across various sectors including healthcare, technology, and
            sustainable development.
          </p>
          <button className="bg-gradient-to-r from-yellow-500 via-emerald-500 to-blue-500 hover:from-blue-500 hover:via-yellow-500 hover:to-emerald-500 text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105">
            Stay Updated
          </button>
        </div>
      </div>
    </section>
  );
};

export default SubsidiariesSection;
